import React, { useMemo } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FaUsers,
  FaDatabase,
  FaServer,
  FaNetworkWired,
  FaChartLine,
  FaUpload,
  FaFileImport,
  FaCogs,
  FaExchangeAlt,
  FaBook,
  FaTachometerAlt,
  FaVideo,
  FaFileCode,
  FaCrosshairs,
  FaUserShield,
  FaFlask,
  FaPlug
} from 'react-icons/fa';
import versionInfo from '../../version.json';

const Sidebar = () => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { videoSources } = useSelector((state) => state.videoSources);
  const { sources } = useSelector((state) => state.sources);
  const { channels } = useSelector((state) => state.channels);

  // Check if user has admin role
  const isAdmin = user?.role === 'ADMIN';

  // Check if user should have access to Video Monitoring page
  // CONSUMER and ADMIN users can both access Video Monitoring
  const hasVideoAccess = useMemo(() => {
    // Allow both ADMIN and CONSUMER roles to access Video Monitoring
    return user?.role === 'ADMIN' || user?.role === 'CONSUMER';
  }, [user?.role]);

  return (
    <div
      className="main-sidebar sidebar d-flex flex-column p-3 text-white bg-dark"
      style={{
        width: '250px',
        minWidth: '250px',
        maxWidth: '250px',
        flexShrink: 0,
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        lineHeight: '1.2',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <Link
        to="/monitoring"
        className="d-flex align-items-center justify-content-center mb-3 mb-md-0 text-white text-decoration-none w-100"
        style={{ fontSize: '24px', fontWeight: '600' }}
      >
        <span className="fs-4" style={{ fontSize: '24px !important', fontWeight: '600 !important' }}>LEADER Cloud</span>
      </Link>
      <hr />
      <div style={{ flex: '1 1 auto', overflowY: 'auto', paddingBottom: '80px' }}>
        <Nav className="flex-column">
          {/* User Interfaces Section */}
          <div className="nav-group">
            <div className="nav-section-header">
              <FaTachometerAlt className="nav-icon" /> User Interfaces
            </div>
            <Nav.Item>
              <Nav.Link
                as={Link}
                to="/monitoring"
                className={location.pathname === '/monitoring' ? 'active' : ''}
              >
                <FaTachometerAlt className="nav-icon" /> Data Monitor
              </Nav.Link>
            </Nav.Item>

            {hasVideoAccess && (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/video-monitoring"
                  className={location.pathname === '/video-monitoring' ? 'active' : ''}
                >
                  <FaVideo className="nav-icon" /> Video Monitor
                </Nav.Link>
              </Nav.Item>
            )}

            {hasVideoAccess && (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/atr"
                  className={location.pathname === '/atr' ? 'active' : ''}
                >
                  <FaCrosshairs className="nav-icon" /> ATR Monitor
                </Nav.Link>
              </Nav.Item>
            )}

            {isAdmin && (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/connections"
                  className={location.pathname === '/connections' ? 'active' : ''}
                >
                  <FaPlug className="nav-icon" /> Connections
                </Nav.Link>
              </Nav.Item>
            )}

            <Nav.Item>
              <Nav.Link
                as={Link}
                to="/records"
                className={location.pathname === '/records' ? 'active' : ''}
              >
                <FaDatabase className="nav-icon" /> Records
              </Nav.Link>
            </Nav.Item>
          </div>

          {/* Admin Section */}
          {isAdmin && (
            <div className="nav-group">
              <div className="nav-section-header">
                <FaUserShield className="nav-icon" /> Admin
              </div>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/users"
                  className={location.pathname === '/users' ? 'active' : ''}
                >
                  <FaUsers className="nav-icon" /> Users
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/schemas"
                  className={location.pathname === '/schemas' ? 'active' : ''}
                >
                  <FaFileCode className="nav-icon" /> Schemas
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/mappings"
                  className={location.pathname === '/mappings' ? 'active' : ''}
                >
                  <FaExchangeAlt className="nav-icon" /> Mappings
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/pipelines"
                  className={location.pathname === '/pipelines' ? 'active' : ''}
                >
                  <FaCogs className="nav-icon" /> Pipelines
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/sensors"
                  className={location.pathname === '/sensors' ? 'active' : ''}
                >
                  <FaChartLine className="nav-icon" /> Sensors
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/platforms"
                  className={location.pathname === '/platforms' ? 'active' : ''}
                >
                  <FaNetworkWired className="nav-icon" /> Platforms
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/suppliers"
                  className={location.pathname === '/suppliers' ? 'active' : ''}
                >
                  <FaServer className="nav-icon" /> Suppliers
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/sources"
                  className={location.pathname === '/sources' ? 'active' : ''}
                >
                  <FaDatabase className="nav-icon" /> Sources
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/video-sources"
                  className={location.pathname === '/video-sources' ? 'active' : ''}
                >
                  <FaVideo className="nav-icon" /> Video Sources
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/channels"
                  className={location.pathname === '/channels' ? 'active' : ''}
                >
                  <FaNetworkWired className="nav-icon" /> Channels
                </Nav.Link>
              </Nav.Item>
            </div>
          )}

          {/* Testing Section */}
          {isAdmin && (
            <div className="nav-group">
              <div className="nav-section-header">
                <FaFlask className="nav-icon" /> Testing
              </div>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/ingest"
                  className={location.pathname === '/ingest' ? 'active' : ''}
                >
                  <FaFileImport className="nav-icon" /> Ingest
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/upload"
                  className={location.pathname === '/upload' ? 'active' : ''}
                >
                  <FaUpload className="nav-icon" /> Upload
                </Nav.Link>
              </Nav.Item>
            </div>
          )}

          {/* Documentation - Inside scrollable area */}
          <div className="nav-documentation">
            <Nav.Item>
              <Nav.Link
                as={Link}
                to="/docs"
                className={`${location.pathname.startsWith('/docs') ? 'active' : ''} docs-nav-item`}
              >
                <FaBook className="nav-icon" /> Documentation
              </Nav.Link>
            </Nav.Item>
          </div>

        </Nav>
      </div>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1rem', backgroundColor: '#343a40' }}>
        <hr style={{ margin: '0 0 0.5rem 0' }} />
        <div className="text-center small">
          <div style={{ color: '#adb5bd' }}>LEADER Cloud</div>
          <div style={{ color: '#adb5bd' }}>{versionInfo.version}</div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
